import { assemblyDocumentId } from '../../components/FileMergeModal/FileMergeModalContainerUtils';
import { isMergeEligibleRecord } from '../../components/TabView/MergeSelectedBar';
import { PDF_FILE_TYPES } from '../../utils/constants';

// Pure logic for the PDF Page Builder. Everything here mirrors the
// server-side assembly contract (dms_backend documents/serializers/
// document_assembly.py): 1-based inclusive page ranges like "4-7,13-20",
// at most 50 items per job, and a 200-character cap per range spec.

export const MAX_RANGE_SPEC_LENGTH = 200;

export const isPdfRecord = (record) =>
  !!record && PDF_FILE_TYPES.includes(record.file_type);

// Same eligibility rule as the merge flow: non-folder PDF/Office records.
export const isBuilderEligibleRecord = isMergeEligibleRecord;

/**
 * Parse a 1-based range spec ("4-7,13,20-") into an ordered list of page
 * numbers. Returns { pages } on success or { error } with a human message.
 * Mirrors the backend grammar so a spec accepted here is accepted there.
 *
 * A null/0 pageCount means the length is unknown (the preview failed to
 * load): the grammar and ordering are still validated, but the upper
 * bound is left to the server — except open-ended tails ("20-"), which
 * cannot be expanded without knowing the last page.
 */
export const parseRangeSpec = (spec, pageCount) => {
  const trimmed = (spec || '').trim();
  if (!trimmed) {
    return { error: 'Enter a page range, e.g. 4-7, 13-20.' };
  }
  const knownCount = pageCount && pageCount >= 1 ? pageCount : null;
  const pages = [];
  for (const rawPart of trimmed.split(',')) {
    const part = rawPart.trim();
    if (!part) {
      return { error: `Empty segment in "${trimmed}".` };
    }
    const match = /^(\d+)\s*(-\s*(\d*))?$/.exec(part);
    if (!match) {
      return { error: `"${part}" is not a valid page or range.` };
    }
    const start = parseInt(match[1], 10);
    let end = start;
    if (match[2] !== undefined) {
      if (match[3]) {
        end = parseInt(match[3], 10);
      } else if (knownCount) {
        end = knownCount;
      } else {
        return {
          error: `"${part}" needs an end page — the document length is not known yet.`,
        };
      }
    }
    if (start < 1 || end < start) {
      return { error: `"${part}" is not a valid range.` };
    }
    if (knownCount && end > knownCount) {
      return {
        error: `"${part}" goes past the last page (${knownCount}).`,
      };
    }
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
  }
  return { pages };
};

/**
 * Collapse an ordered list of 1-based page numbers into the compact spec
 * the assembly API takes: [4,5,6,7,13] -> "4-7,13". Order is preserved;
 * non-consecutive jumps (including descending) start a new segment.
 */
export const pagesToRangeSpec = (pages) => {
  if (!Array.isArray(pages) || pages.length === 0) return '';
  const parts = [];
  let runStart = pages[0];
  let prev = pages[0];
  for (const page of pages.slice(1)) {
    if (page === prev + 1) {
      prev = page;
      continue;
    }
    parts.push(runStart === prev ? `${runStart}` : `${runStart}-${prev}`);
    runStart = page;
    prev = page;
  }
  parts.push(runStart === prev ? `${runStart}` : `${runStart}-${prev}`);
  return parts.join(',');
};

/** "Pages 4-7, 13-20" for segment cards; null pages means every page. */
export const describeSegmentPages = (segment) => {
  if (!segment?.pages) return 'All pages';
  const spec = pagesToRangeSpec(segment.pages).replace(/,/g, ', ');
  const count = segment.pages.length;
  return `Page${count === 1 ? '' : 's'} ${spec} (${count})`;
};

export const segmentPageCount = (segment, sourceNumPages) => {
  if (segment?.pages) return segment.pages.length;
  return sourceNumPages || 0;
};

// Split one segment's spec into <=200-char chunks so a heavily scattered
// selection still fits the serializer's per-item cap. Splits only on
// segment boundaries, so each chunk remains a valid spec.
const splitSpecToItems = (spec) => {
  if (spec.length <= MAX_RANGE_SPEC_LENGTH) return [spec];
  const chunks = [];
  let current = '';
  for (const part of spec.split(',')) {
    const candidate = current ? `${current},${part}` : part;
    if (candidate.length > MAX_RANGE_SPEC_LENGTH && current) {
      chunks.push(current);
      current = part;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);
  return chunks;
};

/**
 * Convert builder segments into assembly API items.
 *
 * segments: [{ sourceKey, pages: number[]|null, label }]
 * sourcesByKey: { [sourceKey]: { record } }
 *
 * Returns { items } or { error }.
 */
export const buildAssemblyItems = (segments, sourcesByKey) => {
  if (!segments || segments.length === 0) {
    return { error: 'Add at least one document or page range to the output.' };
  }
  const items = [];
  for (const segment of segments) {
    const source = sourcesByKey[segment.sourceKey];
    const documentId = assemblyDocumentId(source?.record);
    if (!documentId) {
      return {
        error: `"${segment.sourceName || 'A document'}" could not be identified — remove it and try again.`,
      };
    }
    const base = {};
    if (segment.label && segment.label.trim()) {
      base.label = segment.label.trim();
      if (segment.labelNumbering) {
        base.label_numbering = true;
      }
    }
    if (!segment.pages) {
      items.push({ document_id: documentId, ...base });
      continue;
    }
    if (segment.pages.length === 0) {
      return {
        error: `"${segment.sourceName || 'A document'}" has an empty page selection — remove it or pick pages.`,
      };
    }
    const spec = pagesToRangeSpec(segment.pages);
    const chunks = splitSpecToItems(spec);
    // The server numbers bates-style stamps per ITEM, so a numbered
    // segment split across the 200-char cap would restart at -001 in
    // each chunk. Rare (needs a wildly scattered selection) — refuse
    // rather than emit wrong numbering on a court document.
    if (base.label_numbering && chunks.length > 1) {
      return {
        error:
          `"${segment.sourceName || 'A document'}" has too scattered a page ` +
          'selection to Bates-number as one exhibit — split it into ' +
          'simpler ranges or uncheck Bates stamping.',
      };
    }
    // Every chunk carries the segment's label: the builder always uses the
    // server default label_pages="all", so stamping each chunk reproduces
    // exactly what one unsplit item would have done.
    for (const chunk of chunks) {
      items.push({ document_id: documentId, pages: chunk, ...base });
    }
  }
  return { items };
};

/** Stable key for a source document (matches merge-flow identity rules). */
export const sourceKeyFor = (record) =>
  record?.unique_id || String(record?.id ?? '') || null;

/**
 * Suggested output name. Only promises "(with exhibits)" when the output
 * actually contains more than one item — a single-document build named
 * "… (with exhibits).pdf" reads like the exhibits were silently dropped.
 */
export const defaultBuilderOutputName = (
  seedRecords,
  now = new Date(),
  withExhibits = true
) => {
  const first = seedRecords && seedRecords[0];
  if (first?.name) {
    const base = first.name.replace(/\.[^.]+$/, '');
    return withExhibits ? `${base} (with exhibits).pdf` : `${base}.pdf`;
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `Assembled ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )} ${pad(now.getHours())}${pad(now.getMinutes())}.pdf`;
};
